import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useConfigService } from '@thd-nucleus/experience-context';
import { Alert, Typography, LoadingButton } from '@one-thd/sui-atomic-components';
import className from 'classnames';
import { createBrowserHistory } from 'history';
import CardNumberInput from './CardNumberInput';
import CardPinInput from './CardPinInput';
import { VERBIAGE, containerWidthClass, FEATURE_SWITCH } from '../../utils/consts';
import { getTmxId, isLocalHost, goToAuth } from '../../utils/utils';
import { publishNewRelicPageAction } from '../../utils/new-relic';
import useReCaptcha from '../../hooks/useReCaptcha';
import useKeyPress from '../../hooks/useKeyPress';
import { useGiftcardBalanceCheck } from '../../hooks/useGiftcardBalanceCheck';
import { setScanningAnalytics } from '../../utils/Analytics';

const CheckBalanceForm = (props) => {
  const {
    onSubmit, hideError = false, errorMsg, onError, isDrawer
  } = props;
  const [cardInfo, setCardInfo] = useState(null);
  const containerClasses = 'sui-w-full sui-mb-8';
  const isContinueDisabled = !cardInfo?.cardNumber || !cardInfo?.pinNumber || !!errorMsg;
  const reCaptchaEnabled = !!useConfigService(FEATURE_SWITCH.RECAPTCHA);
  const [isBalanceCheckLoading, setBalanceCheckLoading] = useState(false);
  const [gcCheckBalanceResponse, responseStatus] = useGiftcardBalanceCheck();
  const { data, loading, error } = responseStatus;

  if (!loading && isBalanceCheckLoading) {
    const gcDetail = data?.balanceCheck;
    const errors = error?.graphQLErrors?.[0]?.extensions?.errors
      || error?.graphQLErrors?.[0]?.extensions?.customer_payment_info;
    const description = errors?.[0]?.description;
    const errorCode = errors?.[0]?.errorCode;
    if (errorCode === 'CUST_PAY_ERR_4000') {
      goToAuth();
    } else if (errorCode) {
      publishNewRelicPageAction({
        actionName: 'checkBalanceButtonFail',
        actionObject: {
          sectionName: 'checkBalanceSection',
          message: `Check Balance fail - ${description}`
        }
      });
      onError(description);
    }
    if (gcDetail && gcDetail?.balanceDetails?.[0]?.cardNumber) {
      publishNewRelicPageAction({
        actionName: 'checkBalanceButtonSuccess',
        actionObject: {
          sectionName: 'checkBalanceSection',
          message: 'Check Balance success'
        }
      });
      onSubmit(gcDetail?.balanceDetails?.[0]);
    }
  }

  const handleCardNumber = (number) => {
    setCardInfo({
      ...cardInfo,
      cardNumber: number
    });
    if (errorMsg) {
      onError('');
      setBalanceCheckLoading(false);
    }
  };

  const handlePin = (pin) => {
    setCardInfo({
      ...cardInfo,
      pinNumber: pin
    });
    if (errorMsg) {
      onError('');
      setBalanceCheckLoading(false);
    }
  };

  const handleOnSubmit = async (recaptchaToken) => {
    const { cardNumber, pinNumber } = cardInfo;
    if (!recaptchaToken) {
      publishNewRelicPageAction({
        actionName: 'checkBalanceButtonFail',
        actionObject: {
          sectionName: 'checkBalanceSection',
          message: 'Check Balance fail - missing recaptcha token'
        }
      });
    }
    try {
      await gcCheckBalanceResponse({
        variables: {
          balanceCheck: {
            giftCard: {
              cardNumber,
              pinNumber
            },
            cartAttr: { reCaptcha: recaptchaToken }
          },
          requestContext: {
            tmxProfileId: getTmxId(),
            experience: { type: 'GC_R' }
          }
        }
      });
      setBalanceCheckLoading(true);
    } catch (err) {
      onError(VERBIAGE.technicalError);
      publishNewRelicPageAction({
        actionName: 'checkBalanceButtonFail',
        actionObject: {
          sectionName: 'checkBalanceSection',
          message: 'Check Balance fail - exceptions'
        }
      });
    }
  };

  const { getToken } = useReCaptcha({
    onSubmit: handleOnSubmit
  });

  const handleContinue = () => {
    publishNewRelicPageAction({
      actionName: 'checkBalanceButtonClicked',
      actionObject: { sectionName: 'checkBalanceSection' }
    });
    if (isLocalHost() && !isDrawer) {
      const history = createBrowserHistory();
      history.push('/chapters/reload');
      history.go();
    } else if (reCaptchaEnabled) {
      getToken();
    } else {
      handleOnSubmit('');
    }
  };

  useKeyPress('Enter', () => {
    if (!isContinueDisabled) {
      handleContinue();
    }
  });

  return (
    <div className="sui-w-full sui-h-full sui-flex sui-flex-col sui-items-center sui-justify-center">
      {!isDrawer && (
        <div className={className(containerClasses, 'sui-mt-2')} data-testid="check-balance-title">
          <Typography variant="h1" weight="bold" align="left">
            Check Balance and Reload
          </Typography>
        </div>
      )}
      <div
        className={`sui-max-w-full ${
          isDrawer
            ? 'sui-w-full sui-h-full sui-flex sui-flex-col sui-overflow-auto sui-justify-between'
            : containerWidthClass
        }`}
        data-testid="check-balance-components-container"
      >
        <div className={`${containerClasses}`}>
          {!hideError && errorMsg && <Alert status="error">{errorMsg}</Alert>}
          <div className={containerClasses}>
            <CardNumberInput
              onReady={handleCardNumber}
              onInitScan={() => setScanningAnalytics()}
              onScanSuccess={() => setScanningAnalytics(true)}
            />
          </div>
          <CardPinInput onReady={handlePin} />
        </div>
        <LoadingButton
          loading={loading}
          fullWidth
          variant="primary"
          onClick={handleContinue}
          disabled={isContinueDisabled}
        >
          {VERBIAGE.continue}
        </LoadingButton>
      </div>
    </div>
  );
};

CheckBalanceForm.propTypes = {
  onSubmit: PropTypes.func,
  hideError: PropTypes.bool,
  errorMsg: PropTypes.string,
  onError: PropTypes.func,
  isDrawer: PropTypes.bool
};

CheckBalanceForm.defaultProps = {
  onSubmit: () => {},
  hideError: false,
  errorMsg: '',
  onError: () => {},
  isDrawer: false
};

export default CheckBalanceForm;
